import { Typography } from '@mui/material';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { bindActionCreators } from '@reduxjs/toolkit';

import { VideoRecorderIcon } from 'assets/svg';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { CallReceivedBaseContainer } from 'features/call/call-received/components';
import { handleError as handleErrorThunk } from 'features/notification/store';
import { TeamingInfo } from 'features/teaming/teaming-base/components';
import {
  declineTeamingRequest,
  isHandoffSelector,
  teamingRequestingUserSelector,
} from 'features/teaming/teaming-base/store';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';
import { isEmergencyTeamingCallSelector } from 'features/call/vrs-call/store/vrsCallSelectors';

interface TeamingBaseCallReceivedProps {
  onTakeCallClick: () => void;
}

export const TeamingBaseReceived: FC<TeamingBaseCallReceivedProps> = ({
  onTakeCallClick,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const requestingUser = useAppSelector(teamingRequestingUserSelector);
  const isHandoffReason = useAppSelector(isHandoffSelector);
  const { handleError } = bindActionCreators(
    {
      handleError: handleErrorThunk,
    },
    dispatch
  );
  const isEmergency = useAppSelector(isEmergencyTeamingCallSelector);

  const handleDeclineCall = () => {
    dispatch(declineTeamingRequest());
  };

  const getRequestTitle = (isHandoff: boolean, userName: string) =>
    t(isHandoff ? 'requestForHandoff' : 'requestForTeaming', {
      userName,
    });

  return (
    <CallReceivedBaseContainer
      handleErrorDispatch={handleError}
      primaryButtonOnClick={onTakeCallClick}
      isEmergency={isEmergency}
      secondaryButtonText={isEmergency ? undefined : 'declineCall'}
      secondaryButtonOnClick={isEmergency ? undefined : handleDeclineCall}
    >
      <Icon />
      <Typography
        variant='h1'
        align='center'
        color={theme.palette.common.white}
        mt={14}
        mb={16}
      >
        {getRequestTitle(isHandoffReason, requestingUser.name)}
      </Typography>
      <TeamingInfo />
    </CallReceivedBaseContainer>
  );
};

const Icon = styled(VideoRecorderIcon)`
  margin-top: ${theme.spacing(7)};
`;
